import React, { useState } from "react";
import axios from "axios";
import AdvertiserPanel from "./AdvertiserPanel";
import UnifiedTesterPlayer from "./UnifiedTesterPlayer";
import MessageHeading from "../Pages/CommonComponents/MessageHeading";
import MessageBox from "../../components/MessageBox";

const SSAITestPlayer = () => {
    const [m3u8Url, setM3u8Url] = useState("");
    const [userId, setUserId] = useState("test-user-1");
    const [loading, setLoading] = useState(false);
    const [playbackUrl, setPlaybackUrl] = useState(null);
    const [sessionData, setSessionData] = useState(null);

    const SSAI_API_URL = process.env.REACT_APP_SSAI_API_URL;

    const handleTest = async (e) => {
        e.preventDefault();
        if (!m3u8Url) return;

        setLoading(true);
        setPlaybackUrl(null);
        setSessionData(null);

        try {
            // Send request to SSAI microservice to process the stream
            const response = await axios.post(`${SSAI_API_URL}/api/videos/process`, {
                videoId: "test-video",
                userId: userId,
                tenantId: "default",
                m3u8Url: m3u8Url
            }, {
                headers: { 'x-api-key': process.env.REACT_APP_SSAI_API_KEY || 'ssai_secure_key_2024'}
            });

            if (response.data && response.data.success) {
                const manifestContent = response.data.data.stitchedManifest;
                const blob = new Blob([manifestContent], { type: 'application/vnd.apple.mpegurl' });
                const blobUrl = URL.createObjectURL(blob);
                
                setPlaybackUrl(blobUrl);
                setSessionData(response.data.data);
            }
        } catch (error) {
            console.error("SSAI Processing Error:", error);
            alert("Failed to process stream. Check if SSAI microservice is running on " + SSAI_API_URL);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdvertiserPanel>
            <div className="p-3">
                <div className="theme-bg-color p-3 mt-3 rounded-2 shadow-sm">
                    <MessageHeading text={"SSAI Ad Stitcher & Visual Tester"} className="advertiserHeading" />
                    
                    <div className="row mt-4">
                        <div className="col-lg-5">
                            <div className="card p-4 border-0 shadow-sm mb-4">
                                <h5 className="fw-bold mb-3 theme-text-color">Test Configuration</h5>
                                <form onSubmit={handleTest}>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Source Content URL (.m3u8)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="https://example.com/master.m3u8"
                                            value={m3u8Url}
                                            onChange={(e) => setM3u8Url(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label className="form-label small fw-bold">Simulation User ID</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={userId}
                                            onChange={(e) => setUserId(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn theme-button-bg-color text-white w-100 fw-bold py-2"
                                        disabled={loading}
                                    >
                                        {loading ? "Stitching Ads..." : "Initialize SSAI Session"}
                                    </button>
                                </form>
                                
                                <MessageBox 
                                    classname="mt-4 small p-3 bg-light rounded border-start border-4 border-primary"
                                    text="This tool fetches your content manifest and injects targeted ads based on your active SSAI campaigns. It returns a combined stream for visual verification."
                                />
                            </div>

                            {sessionData && (
                                <div className="card p-4 border-0 shadow-sm animate__animated animate__fadeIn">
                                    <h5 className="fw-bold mb-3 theme-text-color">Session Metadata</h5>
                                    <div className="table-responsive">
                                        <table className="table table-sm small">
                                            <tbody>
                                                <tr>
                                                    <td className="text-muted">Session ID</td>
                                                    <td className="text-end fw-bold">{sessionData.sessionId?.substring(0, 12)}...</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Ad Breaks</td>
                                                    <td className="text-end fw-bold">{sessionData.breakCount}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Ad Duration</td>
                                                    <td className="text-end fw-bold">{sessionData.totalAdDuration}s</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="mt-3">
                                        <label className="small text-muted mb-1">Stitched Manifest URL:</label>
                                        <div className="bg-dark text-success p-2 rounded small text-break font-monospace" style={{ fontSize: '11px' }}>
                                            {sessionData.stitchedManifest}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="col-lg-7">
                            <div className="card p-2 border-0 shadow-sm bg-dark" style={{ minHeight: '400px' }}>
                                {playbackUrl ? (
                                    <div className="animate__animated animate__zoomIn">
                                        <UnifiedTesterPlayer 
                                            url={playbackUrl} 
                                            title="SSAI Preview Stream"
                                        />
                                        <div className="p-3 text-white">
                                            <div className="d-flex align-items-center mb-2">
                                                <div className="spinner-grow spinner-grow-sm text-danger me-2"></div>
                                                <span className="small fw-bold">SSAI INJECTION ACTIVE</span>
                                            </div>
                                            <p className="small text-muted mb-0">Testing ad insertion for User: <strong>{userId}</strong></p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted py-5">
                                        <i className="bi bi-play-circle fs-1 mb-3"></i>
                                        <p>Initialize a session to start visual testing</p>
                                    </div>
                                )}
                            </div>
                            
                            {sessionData?.adBreaks?.length > 0 && (
                                <div className="mt-4 animate__animated animate__fadeInUp">
                                    <h6 className="fw-bold mb-3 theme-text-color">Detected Ad Spans</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {sessionData.adBreaks.map((ad, idx) => (
                                            <div key={idx} className="badge bg-dark border border-secondary p-3 text-start" style={{ minWidth: '150px' }}>
                                                <div className="text-info small mb-1">BREAK {idx + 1}</div>
                                                <div className="fw-bold">Offset: {ad.offsetSeconds}s</div>
                                                <div className="small text-muted">Duration: {ad.duration}s</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AdvertiserPanel>
    );
};

export default SSAITestPlayer;
